import { useEffect } from 'react';

/**
 * Custom hook to inject Schema.org JSON-LD structured data into the page
 * @param {Object|Array} schema - The schema object(s) to inject
 */
export const useSchemaOrg = (schema) => {
  useEffect(() => {
    if (!schema) return;

    // Create script element
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-org-data';
    
    // Handle array of schemas (multiple types on one page)
    const schemaData = Array.isArray(schema) ? schema : [schema];
    script.textContent = JSON.stringify(schemaData.length === 1 ? schemaData[0] : schemaData);
    
    // Remove existing schema script if present
    const existingScript = document.getElementById('schema-org-data');
    if (existingScript) {
      existingScript.remove();
    }
    
    // Append to head
    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      const scriptToRemove = document.getElementById('schema-org-data');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [schema]);
};

/**
 * Generate LocalBusiness schema for Kalamata Airport Taxi
 */
export const getLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://kalamataairporttaxi.com/#business",
  "name": "Kalamata Airport Taxi",
  "alternateName": "Kalamata Airport Transfers",
  "description": "Professional taxi and airport transfer services from Kalamata Airport to Costa Navarino, Stoupa, Kardamyli, Pylos and all Messenia destinations.",
  "url": "https://kalamataairporttaxi.com",
  "telephone": "+30 693 242 1170",
  "email": "info@kalamataairporttaxi.com",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Kalamata International Airport",
    "addressLocality": "Kalamata",
    "addressRegion": "Messenia",
    "postalCode": "24100",
    "addressCountry": "GR"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "37.0683",
    "longitude": "22.0255"
  },
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    "opens": "00:00",
    "closes": "23:59"
  },
  "priceRange": "€€",
  "paymentAccepted": ["Cash", "Credit Card", "Debit Card"],
  "currenciesAccepted": "EUR",
  "areaServed": {
    "@type": "GeoCircle",
    "geoMidpoint": {
      "@type": "GeoCoordinates",
      "latitude": "37.0683",
      "longitude": "22.0255"
    },
    "geoRadius": "100000"
  },
  "serviceType": "Airport Transfer Service",
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "reviewCount": "127",
    "bestRating": "5",
    "worstRating": "1"
  },
  "image": "https://kalamataairporttaxi.com/og-image.jpg",
  "sameAs": []
});

/**
 * Generate FAQPage schema from FAQ data
 * @param {Array} faqs - Array of {question, answer} objects
 */
export const getFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

/**
 * Generate Service schema for a transfer route
 * @param {Object} route - Route data
 * @param {Array} vehicles - Available vehicles with prices
 */
export const getServiceSchema = (route, vehicles) => {
  const minPrice = vehicles.length > 0 
    ? Math.min(...vehicles.map(v => v.price)) 
    : route?.base_price || 0;
  const maxPrice = vehicles.length > 0 
    ? Math.max(...vehicles.map(v => v.price)) 
    : route?.base_price || 0;

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Airport Transfer",
    "name": `Transfer from ${route?.from_name || 'Kalamata Airport'} to ${route?.to_name || 'destination'}`,
    "description": `Private taxi transfer from ${route?.from_name || 'Kalamata Airport'} to ${route?.to_name || 'destination'}. Duration: approximately ${route?.duration_min || 45} minutes.`,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Kalamata Airport Taxi",
      "telephone": "+30 693 242 1170"
    },
    "areaServed": {
      "@type": "Place",
      "name": "Messenia, Greece"
    },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "EUR",
      "lowPrice": minPrice,
      "highPrice": maxPrice,
      "offerCount": vehicles.length
    }
  };
};

/**
 * Generate BreadcrumbList schema
 * @param {Array} items - Array of {name, url} objects
 */
export const getBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

export default useSchemaOrg;
